import { useMemo } from 'react';
import { CustomAttributeSidePanelRow } from 'src/components/CustomAttributes/side-panel-row';
import { CLIENT_SECTION_PROPERTIES } from 'src/components/SidePanelTabs/SiteClient/client-section-registry';
import { SITE_SECTION_PROPERTIES } from 'src/components/SidePanelTabs/SiteClient/site-section-registry';
import { useRelatedCustomAttributeDescriptors } from 'src/graphql/entities/custom-attributes/hooks/use-related-custom-attribute-descriptors';
import {
  buildCustomAttributeSidePanelDescriptors,
  useCustomAttributesSidePanel,
} from 'src/graphql/entities/custom-attributes/side-panel-context';
import { useGroupRules, UserGroupRules } from 'src/graphql/entities/group-rules';
import type {
  SidePanelSectionData,
  SidePanelSurfaceConfig,
} from 'src/graphql/entities/side-panel-personalization/model/descriptors';
import { UserSettingId } from 'src/graphql/entities/user-setting-value/user-setting-value.types';
import {
  CONVERSATION_BASE_PROPERTIES,
  SUMMARY_SECTION_PROPERTIES,
} from './conversation-section-registry';

function useConversationSections(): SidePanelSectionData[] {
  const customAttributes = useCustomAttributesSidePanel();
  const siteAttributes = useRelatedCustomAttributeDescriptors('Address');
  const clientAttributes = useRelatedCustomAttributeDescriptors('Client');
  const { checkValidity } = useGroupRules();
  // user-group rule, not partner-scoped allowedRules — omit the toggle so the
  // modal never shows Summary to users without access
  const canSeeSummary = checkValidity([UserGroupRules.AccessToAiConversationSummary]);

  return useMemo(
    () => [
      {
        key: 'conversation' as const,
        label: 'Conversation',
        loading: customAttributes.loading,
        notice: customAttributes.payloadError ?? undefined,
        hidePanelLabel: true,
        properties: [
          ...(canSeeSummary ? SUMMARY_SECTION_PROPERTIES : []),
          ...CONVERSATION_BASE_PROPERTIES,
          ...buildCustomAttributeSidePanelDescriptors(
            customAttributes.rowsByPropertyKey,
            CustomAttributeSidePanelRow,
          ),
        ],
      },
      // card-rendered: fields, custom attributes included, show on the site/client cards
      {
        key: 'site' as const,
        label: 'Site',
        loading: siteAttributes.loading,
        properties: [...SITE_SECTION_PROPERTIES, ...siteAttributes.properties],
      },
      {
        key: 'client' as const,
        label: 'Client',
        loading: clientAttributes.loading,
        reorderable: true,
        properties: [...CLIENT_SECTION_PROPERTIES, ...clientAttributes.properties],
      },
    ],
    [
      canSeeSummary,
      customAttributes.loading,
      customAttributes.payloadError,
      customAttributes.rowsByPropertyKey,
      siteAttributes,
      clientAttributes,
    ],
  );
}

export const conversationSidePanelSurface: SidePanelSurfaceConfig = {
  surfaceKey: 'conversation',
  settingIds: {
    profiles: UserSettingId.ConversationSidePanelProfiles,
    personalization: UserSettingId.ConversationSidePanelPersonalization,
    groupProfile: UserSettingId.ConversationSidePanelGroupProfile,
  },
  useSections: useConversationSections,
};
